/**
 * PATH: packages/ui/src/shortcuts/dispatcher.ts
 * FILE_ID: UI-SHORTCUTS-DISPATCHER-001
 * PURPOSE: Pure core of the central keyboard dispatcher — resolve which
 *          registered action (if any) a KeyboardEvent should fire, honouring
 *          scope precedence (screen > hub > global), text-input guards, and
 *          user overrides. No React, no DOM listeners (that's ShortcutProvider).
 * WHY: A single resolver replaces the per-hub ad-hoc keydown handlers and
 *      makes precedence + guards designed-and-tested instead of incidental.
 *
 * FLOW:
 *   ┌───────────────────────────┐   ┌──────────────────────┐
 *   │ actions + overrides + ctx │ → │ resolveShortcut(...)  │ → ShortcutAction|null
 *   │ + KeyboardEvent + target  │   │ (precedence + guards) │
 *   └───────────────────────────┘   └──────────────────────┘
 *
 * RELATED:
 *   - ./binding.ts: matchBinding (single-chord match)
 *   - ./overrides.ts: effectiveShortcut
 *   - ./ShortcutProvider.tsx: wires this to one document listener
 */

import { matchBinding } from './binding'
import { effectiveShortcut } from './overrides'
import type {
  Platform,
  ShortcutAction,
  ShortcutContext,
  ShortcutOverrideMap,
  ShortcutScope,
} from './types'

/** Editable target = typing surface; guarded actions are suppressed there. */
export function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || !(target as HTMLElement).tagName) return false
  const el = target as HTMLElement
  const tag = el.tagName.toLowerCase()
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true
  if (el.isContentEditable) return true
  return false
}

/** Higher = wins. screen (3) > hub (2) > global (1). */
function scopeRank(scope: ShortcutScope): number {
  if (scope.kind === 'screen') return 3
  if (scope.kind === 'hub') return 2
  return 1
}

/** Is the action active given the current route context? Pure. */
export function isInScope(scope: ShortcutScope, ctx: ShortcutContext): boolean {
  if (scope.kind === 'global') return true
  if (scope.kind === 'hub') return ctx.hub === scope.hub
  return ctx.screen === scope.screen
}

export interface ResolveInput {
  actions: ShortcutAction[]
  overrides: ShortcutOverrideMap
  context: ShortcutContext
  event: Pick<KeyboardEvent, 'key' | 'metaKey' | 'ctrlKey' | 'shiftKey' | 'altKey'>
  platform: Platform
  /** The event target, used for the text-input guard. */
  target?: EventTarget | null
}

/**
 * resolveShortcut — pick the single action to fire, or null.
 *
 * An action is a candidate when it is: in the current scope, enabled (after
 * overrides), has a bound single-chord binding that matches the event, and is
 * not suppressed by the text-input guard (unless allowInInput). Among
 * candidates the highest scope rank wins; ties resolve to registration order.
 * Pure — callers run the side effect.
 */
export function resolveShortcut(input: ResolveInput): ShortcutAction | null {
  const { actions, overrides, context, event, platform, target } = input
  const editable = isEditableTarget(target ?? null)

  let winner: ShortcutAction | null = null
  let winnerRank = -1

  for (const action of actions) {
    if (!isInScope(action.scope, context)) continue
    if (editable && !action.allowInInput) continue

    const eff = effectiveShortcut(action, overrides[action.id])
    if (!eff.enabled || !eff.binding) continue
    if (!matchBinding(eff.binding, event, platform)) continue

    const rank = scopeRank(action.scope)
    if (rank > winnerRank) {
      winner = action
      winnerRank = rank
    }
  }
  return winner
}
